/**
 * AISettings - Configure AI features for an event
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Sparkles, Settings, Key, Eye, EyeOff, Loader2, Wand2, MessageSquare, Mic, CheckCircle2, XCircle } from 'lucide-react';
import { aiAPI } from '@/lib/api';

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'] },
  { id: 'anthropic', name: 'Anthropic', models: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229', 'claude-3-haiku-20240307'] },
  { id: 'google', name: 'Google', models: ['gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-1.0-pro'] },
];

const AISettings = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  
  const { data: config, isLoading } = useQuery({
    queryKey: ['aiConfig', eventId],
    queryFn: () => aiAPI.getConfig(eventId).then(r => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => aiAPI.updateConfig(eventId, data),
    onSuccess: () => {
      toast.success('AI configuration updated');
      queryClient.invalidateQueries(['aiConfig', eventId]);
      setApiKey('');
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update config'),
  });

  const handleToggle = (field, value) => {
    updateMutation.mutate({ [field]: value });
  };

  const handleFeatureToggle = (feature, value) => {
    updateMutation.mutate({
      features: { ...config?.features, [feature]: value }
    });
  };

  const handleSaveApiKey = () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    updateMutation.mutate({ api_key: apiKey });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const selectedProvider = PROVIDERS.find(p => p.id === config?.provider) || PROVIDERS[0];

  return (
    <div className="space-y-6" data-testid="ai-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold">AI Features</h2>
            <p className="text-sm text-slate-500">Configure AI-powered summaries, Q&A, and transcription</p>
          </div>
        </div>
        <Badge variant={config?.enabled ? 'default' : 'secondary'}>
          {config?.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config"><Settings className="h-4 w-4 mr-2" />Configuration</TabsTrigger>
          <TabsTrigger value="features"><Wand2 className="h-4 w-4 mr-2" />Features</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4 mt-4">
          {/* Master Enable Toggle */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">Enable AI Features</h3>
                  <p className="text-sm text-slate-500">Turn on AI-powered features for this event</p>
                </div>
                <Switch
                  checked={config?.enabled || false}
                  onCheckedChange={(v) => handleToggle('enabled', v)}
                  data-testid="ai-enable-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Provider Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AI Provider</CardTitle>
              <CardDescription>Select your preferred AI provider and model</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Provider</Label>
                  <Select 
                    value={config?.provider || 'openai'} 
                    onValueChange={(v) => handleToggle('provider', v)}
                  >
                    <SelectTrigger data-testid="provider-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Model</Label>
                  <Select 
                    value={config?.model || selectedProvider.models[0]} 
                    onValueChange={(v) => handleToggle('model', v)}
                  >
                    <SelectTrigger data-testid="model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProvider.models.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Key Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Key
              </CardTitle>
              <CardDescription>
                Enter your {selectedProvider.name} API key. Keys are encrypted and stored securely.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                {config?.has_api_key ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    API Key Configured
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-50 text-red-700">
                    <XCircle className="h-3 w-3 mr-1" />
                    No API Key
                  </Badge>
                )}
              </div>
              
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={config?.has_api_key ? 'Enter new key to replace' : 'Enter API key'}
                    data-testid="api-key-input"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button onClick={handleSaveApiKey} disabled={!apiKey.trim()} data-testid="save-key-btn">
                  Save Key
                </Button>
              </div>

              <p className="text-xs text-slate-400">
                Get your API key from: 
                {config?.provider === 'openai' && <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 ml-1">OpenAI Dashboard</a>}
                {config?.provider === 'anthropic' && <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 ml-1">Anthropic Console</a>}
                {config?.provider === 'google' && <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-500 ml-1">Google AI Studio</a>}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="space-y-4 mt-4">
          {/* Session Summaries */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Wand2 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Session Summaries</h3>
                    <p className="text-sm text-slate-500">AI-generated summaries of session content and key takeaways</p>
                  </div>
                </div>
                <Switch
                  checked={config?.features?.summaries !== false}
                  onCheckedChange={(v) => handleFeatureToggle('summaries', v)}
                  data-testid="summaries-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Smart Q&A */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Smart Q&A</h3>
                    <p className="text-sm text-slate-500">AI-powered question answering about sessions and content</p>
                  </div>
                </div>
                <Switch
                  checked={config?.features?.qa !== false}
                  onCheckedChange={(v) => handleFeatureToggle('qa', v)}
                  data-testid="qa-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Live Transcription */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Mic className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Live Transcription</h3>
                    <p className="text-sm text-slate-500">Real-time speech-to-text for session recordings (requires OpenAI)</p>
                  </div>
                </div>
                <Switch
                  checked={config?.features?.transcription !== false}
                  onCheckedChange={(v) => handleFeatureToggle('transcription', v)}
                  data-testid="transcription-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AISettings;
